import { Zap, Search, X, PlusCircle, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import {
	ConditioningTemplateEditor,
	CONDITIONING_TYPES,
} from "../ConditioningTemplateEditor";
import {
	getForTimeRoundsDisplay,
	normalizeEmomRoundTemplates,
} from "../../lib/programBuilderUtils";

export function ConditioningForm({
	newExercise,
	setNewExercise,
	resultSchemaFocusToken = 0,
	conditioningTemplates,
	exercises,
	setExercises,
	onQuickSaveTemplate,
	isSavingTemplate = false,
	t,
}) {
	const canQuickSave =
		typeof onQuickSaveTemplate === "function" &&
		!!newExercise?.conditioning_type &&
		!newExercise?.library_conditioning_id;

	return (
		<div className="space-y-3">
			{/* Library templates picker — compact */}
			{conditioningTemplates.length > 0 && (
				<div className="space-y-1">
					{!newExercise._condLibraryOpen ? (
						<button
							type="button"
							onClick={() =>
								setNewExercise({
									...newExercise,
									_condLibraryOpen: true,
									_condLibrarySearch: "",
								})
							}
							className="w-full flex items-center gap-2 px-3 py-1.5 rounded-sm border border-dashed border-yellow-400/40 text-yellow-400 text-xs hover:bg-yellow-400/5 transition-colors"
						>
							<Zap className="w-3 h-3" />
							<span>{t("pbCondUseLibraryTemplate")}</span>
							<span className="ml-auto text-[10px] text-muted-foreground">
								{conditioningTemplates.length} {t("pbCondTemplatesAvailable")}
							</span>
						</button>
					) : (
						<div className="space-y-1.5">
							<div className="flex items-center gap-2">
								<div className="relative flex-1">
									<Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
									<Input
										value={newExercise._condLibrarySearch || ""}
										onChange={(e) =>
											setNewExercise({
												...newExercise,
												_condLibrarySearch: e.target.value,
											})
										}
										className="bg-secondary border-transparent rounded-sm h-7 text-xs pl-7"
										placeholder={t("pbCondSearchTemplate")}
										autoFocus
									/>
								</div>
								<button
									type="button"
									onClick={() =>
										setNewExercise({
											...newExercise,
											_condLibraryOpen: false,
											_condLibrarySearch: "",
										})
									}
									className="text-muted-foreground hover:text-foreground p-1"
								>
									<X className="w-3.5 h-3.5" />
								</button>
							</div>
							<div className="max-h-32 overflow-y-auto space-y-1">
								{conditioningTemplates
									.filter(
										(tpl) =>
											!newExercise._condLibrarySearch ||
											tpl.name
												.toLowerCase()
												.includes(
													(newExercise._condLibrarySearch || "").toLowerCase(),
												),
									)
									.map((tpl) => {
										const isSelected =
											newExercise.library_conditioning_id ===
											tpl.conditioning_id;
										const typeLabel =
											CONDITIONING_TYPES.find(
												(ct) => ct.value === tpl.conditioning_type,
											)?.label || tpl.conditioning_type;
										const templateForTimeRoundsDisplay =
											tpl.conditioning_type === "for_time"
												? getForTimeRoundsDisplay(tpl.rounds)
												: "";
										return (
											<button
												key={tpl.conditioning_id}
												type="button"
												onClick={() => {
													setNewExercise({
														...newExercise,
														library_conditioning_id: tpl.conditioning_id,
														conditioning_type: tpl.conditioning_type,
														time_cap_seconds: tpl.time_cap_seconds,
														rounds: tpl.rounds,
														interval_seconds: tpl.interval_seconds,
														emom_conditioning_interval:
															tpl.emom_conditioning_interval ||
															(Number.isFinite(Number(tpl.interval_seconds))
																? `${Math.floor(
																		Number(tpl.interval_seconds) / 60,
																	)}:${String(
																		Number(tpl.interval_seconds) % 60,
																	).padStart(2, "0")}`
																: ""),
														emom_rounds: normalizeEmomRoundTemplates(tpl),
														hiit_cycles: tpl.hiit_cycles ?? null,
														hiit_work_mode: tpl.hiit_work_mode || "for_time",
														hiit_work_seconds: tpl.hiit_work_seconds ?? null,
														hiit_work_interval:
															tpl.hiit_work_interval ||
															(Number.isFinite(Number(tpl.hiit_work_seconds))
																? `${Math.floor(
																		Number(tpl.hiit_work_seconds) / 60,
																	)}:${String(
																		Number(tpl.hiit_work_seconds) % 60,
																	).padStart(2, "0")}`
																: ""),
														hiit_work_rounds: tpl.hiit_work_rounds || "",
														hiit_work_custom_text:
															tpl.hiit_work_custom_text || "",
														hiit_rest_mode: tpl.hiit_rest_mode || "fixed",
														hiit_rest_seconds: tpl.hiit_rest_seconds ?? null,
														hiit_rest_interval:
															tpl.hiit_rest_interval ||
															(Number.isFinite(Number(tpl.hiit_rest_seconds))
																? `${Math.floor(
																		Number(tpl.hiit_rest_seconds) / 60,
																	)}:${String(
																		Number(tpl.hiit_rest_seconds) % 60,
																	).padStart(2, "0")}`
																: ""),
														hiit_rest_ratio: tpl.hiit_rest_ratio || "",
														conditioning_exercises: (
															tpl.conditioning_exercises || []
														).map((ce) => ({
															exercise_id: ce.exercise_id || "",
															exercise_name: ce.exercise_name || "",
															reps: ce.reps || "",
															load: ce.load || "",
															notes: ce.notes || "",
														})),
														conditioning_free_text:
															tpl.conditioning_free_text || "",
														result_schema: Array.isArray(tpl.result_schema)
															? tpl.result_schema
															: [],
														exercise_name: tpl.name,
														_condLibraryOpen: false,
														_condLibrarySearch: "",
													});
												}}
												className={`w-full text-left px-3 py-1.5 rounded-sm border text-xs transition-colors ${
													isSelected
														? "border-yellow-400 bg-yellow-400/10 text-yellow-400"
														: "border-border text-muted-foreground hover:border-yellow-400/50"
												}`}
											>
												<div className="flex items-center gap-2">
													<Zap className="w-3 h-3 text-yellow-400 shrink-0" />
													<span className="font-medium text-foreground truncate">
														{tpl.name}
													</span>
													{tpl.library_scope && (
														<span
															className={`text-[10px] px-1 py-0.5 rounded-sm uppercase tracking-wider ${
																tpl.library_scope === "global"
																	? "bg-amber-500/20 border border-amber-500/40 text-amber-300"
																	: "bg-emerald-500/20 border border-emerald-500/40 text-emerald-300"
															}`}
														>
															{tpl.library_scope === "global"
																? t("libraryScopeGlobalSingle")
																: t("libraryScopePersonal")}
														</span>
													)}
													<span className="text-[10px] text-muted-foreground ml-auto shrink-0">
														{typeLabel}
													</span>
												</div>
												{templateForTimeRoundsDisplay && (
													<div className="mt-1 text-[10px] text-yellow-400">
														{templateForTimeRoundsDisplay}
													</div>
												)}
											</button>
										);
									})}
							</div>
						</div>
					)}
				</div>
			)}

			{/* Show selected template indicator */}
			{newExercise.library_conditioning_id && !newExercise._condLibraryOpen && (
				<div className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-yellow-400/30 bg-yellow-400/5 text-xs">
					<Zap className="w-3 h-3 text-yellow-400 shrink-0" />
					<span className="text-foreground font-medium truncate">
						{newExercise.exercise_name}
					</span>
					<button
						type="button"
						onClick={() =>
							setNewExercise({
								...newExercise,
								library_conditioning_id: "",
								conditioning_type: "",
								time_cap_seconds: "",
								rounds: "",
								interval_seconds: "",
								emom_conditioning_interval: "",
								emom_rounds: [],
								hiit_cycles: null,
								hiit_work_mode: "for_time",
								hiit_work_seconds: null,
								hiit_work_interval: "",
								hiit_work_rounds: "",
								hiit_work_custom_text: "",
								hiit_rest_mode: "fixed",
								hiit_rest_seconds: null,
								hiit_rest_interval: "",
								hiit_rest_ratio: "",
								conditioning_exercises: [],
								conditioning_free_text: "",
								result_schema: [],
								exercise_name: "",
							})
						}
						className="ml-auto text-muted-foreground hover:text-foreground"
					>
						<X className="w-3 h-3" />
					</button>
				</div>
			)}

			<div className="border-t border-border pt-2">
				<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
					{t("orCreateManually")}
				</span>
			</div>

			<div className="space-y-1">
				<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
					{t("exerciseName")}
				</span>
				<Input
					value={newExercise.exercise_name ?? ""}
					onChange={(e) =>
						setNewExercise({
							...newExercise,
							exercise_name: e.target.value,
						})
					}
					className="bg-secondary border-transparent rounded-sm text-xs"
					placeholder={
						newExercise.conditioning_type === "free" ||
						newExercise.conditioning_type === "custom"
							? ""
							: t("exerciseNamePlaceholder")
					}
				/>
				{newExercise.conditioning_type !== "free" &&
					newExercise.conditioning_type !== "custom" && (
						<p className="text-[10px] text-muted-foreground">
							{t("conditioningNameAutoHint")}
						</p>
					)}
				{canQuickSave && (
					<button
						type="button"
						onClick={() => onQuickSaveTemplate(newExercise)}
						disabled={isSavingTemplate}
						className="w-full mt-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-sm border border-dashed border-emerald-500/40 text-emerald-300 text-xs hover:bg-emerald-500/5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
					>
						{isSavingTemplate ? (
							<Loader2 className="w-3.5 h-3.5 animate-spin" />
						) : (
							<PlusCircle className="w-3.5 h-3.5" />
						)}
						<span>
							{isSavingTemplate
								? t("pbCondSavingToLibrary")
								: t("pbCondSaveToLibrary")}
						</span>
					</button>
				)}
				{!!newExercise?.library_conditioning_id && (
					<p className="text-[10px] text-emerald-300/90">
						{t("pbCondLinkedToLibrary")}
					</p>
				)}
			</div>

			<ConditioningTemplateEditor
				value={newExercise}
				setValue={(updater) => {
					setNewExercise((prev) => {
						const next =
							typeof updater === "function" ? updater(prev) : updater;
						// Sync exercise_name with type label and clear library link
						if (next.conditioning_type !== prev.conditioning_type) {
							const nextTypeLabel =
								CONDITIONING_TYPES.find(
									(ct) => ct.value === next.conditioning_type,
								)?.label || "";
							const prevTypeLabel =
								CONDITIONING_TYPES.find(
									(ct) => ct.value === prev.conditioning_type,
								)?.label || "";
							const prevCustomName =
								typeof prev.exercise_name === "string"
									? prev.exercise_name.trim()
									: "";
							const normalizedPrevName = prevCustomName.toLowerCase();
							const isAutoGeneratedCustomName = [
								"custom",
								"conditioning",
								"conditioning-custom",
								"conditioning - custom",
							].includes(normalizedPrevName);
							const shouldAutoRename =
								!prevCustomName ||
								normalizedPrevName === prevTypeLabel.toLowerCase() ||
								isAutoGeneratedCustomName;
							return {
								...next,
								exercise_name: shouldAutoRename
									? next.conditioning_type === "free" ||
										next.conditioning_type === "custom"
										? "CONDITIONING"
										: nextTypeLabel
									: prev.exercise_name,
								library_conditioning_id: null,
							};
						}
						return next;
					});
				}}
				exercises={exercises}
				showNameField={false}
				resultSchemaFocusToken={resultSchemaFocusToken}
				onExerciseCreated={(ex) => setExercises((prev) => [...prev, ex])}
			/>
		</div>
	);
}
